import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Wand2, RefreshCw, ExternalLink, Pencil, Check, Palette, Monitor, Smartphone,
  CheckCircle2, Columns2,
} from "lucide-react";
import {
  VISUAL_STYLE_OPTIONS, BRAND_PERSONALITY_OPTIONS, VISUAL_PREF_OPTIONS, MOOD_BY_INDUSTRY, detectIndustryKey,
} from "@/lib/designDna";

const Chip = ({ label, active, disabled, onClick, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`px-3.5 py-1.5 text-xs border transition-colors flex items-center gap-1.5 ${active ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}>
    {active && <Check size={11} />} {label}
  </button>
);

const ChipRow = ({ title, options, selected, onToggle, max, prefix }) => {
  const atMax = max && selected.length >= max;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-gray-400">{title}</div>
        {max && <div className="text-[11px] text-gray-500">{selected.length}/{max}</div>}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = selected.includes(o);
          return <Chip key={o} label={o} active={active} disabled={atMax && !active}
            onClick={() => onToggle(o)} testid={`${prefix}-${o}`} />;
        })}
      </div>
    </div>
  );
};

const ReadChips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.length ? items.map((i) => <span key={i} className="text-[11px] border border-gold/25 text-gold/90 px-2 py-0.5 rounded-full">{i}</span>)
      : <span className="text-xs text-gray-500">Not set yet</span>}
  </div>
);

export default function DesignStudio() {
  const [dna, setDna] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mockups, setMockups] = useState([]);
  const [current, setCurrent] = useState(null); // {id, html, variation, ai_connected}
  const [generating, setGenerating] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [selectedId, setSelectedId] = useState(null);
  const [using, setUsing] = useState(false);
  const [compare, setCompare] = useState(false);
  const [leftId, setLeftId] = useState(null);
  const [rightId, setRightId] = useState(null);
  const [htmlMap, setHtmlMap] = useState({});

  const load = useCallback(async () => {
    try {
      const [d, list, latest] = await Promise.all([
        api.get("/portal/design-dna"),
        api.get("/portal/design-mockups"),
        api.get("/portal/design-mockup/latest"),
      ]);
      setDna(d.data.design_dna);
      const ms = list.data.mockups || [];
      setMockups(ms);
      const sel = ms.find((m) => m.selected);
      if (sel) setSelectedId(sel.id);
      if (latest.data && latest.data.html) { setCurrent(latest.data); setHtmlMap((h) => ({ ...h, [latest.data.id]: latest.data.html })); }
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ensureHtml = useCallback(async (id) => {
    if (!id || htmlMap[id]) return;
    try { const { data } = await api.get(`/portal/design-mockup/${id}`); setHtmlMap((h) => ({ ...h, [id]: data.html })); } catch (e) { /* silent */ }
  }, [htmlMap]);

  const use = async (id) => {
    setUsing(true);
    try {
      await api.post(`/portal/design-mockup/${id}/use`);
      setSelectedId(id);
      setMockups((ms) => ms.map((m) => ({ ...m, selected: m.id === id })));
      toast.success("Sent to your ATF build team as your starting design. 🎉");
    } catch (e) { toast.error(formatApiError(e)); } finally { setUsing(false); }
  };

  const openCompare = () => {
    const l = mockups[0]?.id, r = mockups[1]?.id;
    setLeftId(l); setRightId(r); ensureHtml(l); ensureHtml(r); setCompare(true);
  };

  const startEdit = () => {
    setDraft({
      visual_style: [...(dna.visual_style || [])],
      brand_personality: [...(dna.brand_personality || [])],
      visual_preferences: [...(dna.visual_preferences || [])],
      design_mood: [...(dna.design_mood || [])],
      design_feeling: dna.design_feeling || "",
      colors: dna.colors || "",
    });
    setEditing(true);
  };
  const toggle = (k, v, max) => setDraft((p) => {
    const has = p[k].includes(v);
    if (!has && max && p[k].length >= max) return p;
    return { ...p, [k]: has ? p[k].filter((x) => x !== v) : [...p[k], v] };
  });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/portal/design-dna", draft);
      setDna(data.design_dna); setEditing(false);
      toast.success("Design DNA updated — regenerate to see it applied.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const generate = async (variation) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/portal/design-mockup/generate", { variation });
      setCurrent(data);
      setHtmlMap((h) => ({ ...h, [data.id]: data.html }));
      setMockups((m) => [{ id: data.id, variation: data.variation, ai_connected: data.ai_connected, created_at: data.created_at, org_name: data.org_name }, ...m]);
      toast.success(data.ai_connected ? "Homepage concept generated!" : "Concept generated (offline template).");
    } catch (e) { toast.error(formatApiError(e)); } finally { setGenerating(false); }
  };
  const loadMockup = async (id) => {
    try { const { data } = await api.get(`/portal/design-mockup/${id}`); setCurrent(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  const openFull = () => {
    if (!current?.html) return;
    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(current.html); w.document.close(); }
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="animate-spin" size={18} /> Loading your Design Studio…</div>;
  if (!dna) return null;

  const industryKey = detectIndustryKey(dna.industry);
  const moodOptions = industryKey ? MOOD_BY_INDUSTRY[industryKey] : [];

  return (
    <div className="mb-10" data-testid="design-studio">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="font-heading text-xl text-white flex items-center gap-2"><Palette className="text-gold" size={20} /> Design DNA & Homepage Concept</h2>
        {!editing && <button onClick={startEdit} data-testid="design-edit-btn" className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-gold border border-white/15 hover:border-gold/50 px-3 py-1.5 transition-colors"><Pencil size={14} /> Edit creative direction</button>}
      </div>
      <p className="text-gray-400 text-sm mb-5">This is the creative direction Top Foster uses to design your site. Edit it anytime and instantly generate a fresh homepage concept.</p>

      {/* DNA panel */}
      <div className="bg-navy-800 border border-gold/20 p-5 mb-6">
        {!editing ? (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            <div><div className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Visual Style</div><ReadChips items={dna.visual_style || []} /></div>
            <div><div className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Brand Personality</div><ReadChips items={dna.brand_personality || []} /></div>
            <div><div className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Visual Preferences</div><ReadChips items={dna.visual_preferences || []} /></div>
            <div><div className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">{industryKey ? `${industryKey.charAt(0).toUpperCase() + industryKey.slice(1)} Mood` : "Mood"}</div><ReadChips items={dna.design_mood || []} /></div>
            <div><div className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Brand Colors</div><div className="text-sm text-gray-200">{dna.colors || "—"}</div></div>
            <div className="sm:col-span-2"><div className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Desired Feeling</div><div className="text-sm text-gray-200">{dna.design_feeling || "—"}</div></div>
          </div>
        ) : (
          <div className="space-y-5">
            <ChipRow title="Visual style (up to 5)" options={VISUAL_STYLE_OPTIONS} selected={draft.visual_style} onToggle={(v) => toggle("visual_style", v, 5)} max={5} prefix="ds-style" />
            <ChipRow title="Brand personality" options={BRAND_PERSONALITY_OPTIONS} selected={draft.brand_personality} onToggle={(v) => toggle("brand_personality", v)} prefix="ds-pers" />
            <ChipRow title="Visual preferences" options={VISUAL_PREF_OPTIONS} selected={draft.visual_preferences} onToggle={(v) => toggle("visual_preferences", v)} prefix="ds-pref" />
            {moodOptions.length > 0 && <ChipRow title={`${industryKey.charAt(0).toUpperCase() + industryKey.slice(1)} mood`} options={moodOptions} selected={draft.design_mood} onToggle={(v) => toggle("design_mood", v)} prefix="ds-mood" />}
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Desired feeling</div>
              <textarea rows={2} value={draft.design_feeling} onChange={(e) => setDraft((p) => ({ ...p, design_feeling: e.target.value }))} data-testid="ds-feeling"
                className="w-full bg-navy border border-white/20 text-white p-3 text-sm focus:border-gold focus:outline-none" placeholder="Describe the feeling visitors should have…" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Brand colors</div>
              <input value={draft.colors} onChange={(e) => setDraft((p) => ({ ...p, colors: e.target.value }))} data-testid="ds-colors"
                className="w-full bg-navy border border-white/20 text-white p-3 text-sm focus:border-gold focus:outline-none" placeholder="e.g. Navy & gold, or hex codes" />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={save} disabled={saving} data-testid="design-save-btn" className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-5 py-2.5 text-sm hover:bg-gold-light transition-colors disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Save direction</button>
              <button onClick={() => setEditing(false)} className="text-sm text-gray-400 hover:text-white px-4">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Generate / preview */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <button onClick={() => generate(0)} disabled={generating} data-testid="design-generate-btn"
          className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-5 py-2.5 text-sm hover:bg-gold-light transition-colors disabled:opacity-60">
          {generating ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} {current ? "Generate concept" : "Generate homepage concept"}
        </button>
        {current && (
          <button onClick={() => generate((current.variation || 0) + 1)} disabled={generating} data-testid="design-regenerate-btn"
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            <RefreshCw size={15} /> Regenerate variation
          </button>
        )}
        {mockups.length >= 2 && (
          <button onClick={() => (compare ? setCompare(false) : openCompare())} data-testid="design-compare-btn"
            className={`inline-flex items-center gap-2 border px-5 py-2.5 text-sm transition-colors ${compare ? "border-gold text-gold bg-gold/10" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>
            <Columns2 size={15} /> {compare ? "Exit compare" : "Compare concepts"}
          </button>
        )}
      </div>

      {/* Compare two concepts side by side */}
      {compare && (
        <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="design-compare">
          {[[leftId, setLeftId], [rightId, setRightId]].map(([sideId, setSide], idx) => (
            <div key={idx} className="bg-navy-800 border border-white/10">
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-white/10">
                <select value={sideId || ""} onChange={(e) => { setSide(e.target.value); ensureHtml(e.target.value); }}
                  data-testid={`compare-select-${idx}`} className="bg-navy border border-white/20 text-white text-xs px-2 py-1.5 focus:border-gold focus:outline-none max-w-[60%]">
                  {mockups.map((m) => <option key={m.id} value={m.id}>Variation {(m.variation || 0) + 1} · {new Date(m.created_at).toLocaleDateString()}</option>)}
                </select>
                <button onClick={() => sideId && use(sideId)} disabled={using} data-testid={`compare-use-${idx}`}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 font-semibold transition-colors ${selectedId === sideId ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-gold text-navy hover:bg-gold-light"}`}>
                  {selectedId === sideId ? <><CheckCircle2 size={13} /> Chosen</> : <>Use this</>}
                </button>
              </div>
              <div className="p-2 bg-navy">
                {htmlMap[sideId] ? <iframe title={`concept-${idx}`} srcDoc={htmlMap[sideId]} sandbox="allow-scripts" className="bg-white w-full border border-white/10" style={{ height: 520 }} />
                  : <div className="h-[520px] flex items-center justify-center text-gray-500 text-sm"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {generating && !current && (
        <div className="bg-navy-800 border border-white/10 p-10 text-center text-gray-400" data-testid="design-generating">
          <Loader2 className="animate-spin text-gold mx-auto mb-3" size={22} /> Designing your homepage from your Design DNA…
        </div>
      )}

      {current && (
        <div className="bg-navy-800 border border-white/10" data-testid="design-preview">
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2 text-sm text-white">
              <Sparkles size={14} className="text-gold" /> Homepage concept · variation {(current.variation || 0) + 1}
              <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full border ${current.ai_connected ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`}>{current.ai_connected ? "AI Drafted" : "Template Draft"}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => use(current.id)} disabled={using} data-testid="design-use-btn"
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 font-semibold transition-colors ${selectedId === current.id ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-gold text-navy hover:bg-gold-light"}`}>
                {using ? <Loader2 className="animate-spin" size={13} /> : selectedId === current.id ? <CheckCircle2 size={13} /> : <CheckCircle2 size={13} />} {selectedId === current.id ? "Chosen for build" : "Use this concept"}
              </button>
              <button onClick={() => setDevice("desktop")} className={`p-1.5 border ${device === "desktop" ? "border-gold text-gold" : "border-white/15 text-gray-400"}`} title="Desktop"><Monitor size={15} /></button>
              <button onClick={() => setDevice("mobile")} className={`p-1.5 border ${device === "mobile" ? "border-gold text-gold" : "border-white/15 text-gray-400"}`} title="Mobile"><Smartphone size={15} /></button>
              <button onClick={openFull} data-testid="design-open-full" className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-gold border border-white/15 px-3 py-1.5"><ExternalLink size={13} /> Open full</button>
            </div>
          </div>
          <div className="p-4 bg-navy flex justify-center">
            <iframe title="Homepage concept" srcDoc={current.html} sandbox="allow-scripts"
              className="bg-white border border-white/10 transition-all"
              style={{ width: device === "mobile" ? 390 : "100%", height: 640 }} />
          </div>
        </div>
      )}

      {/* Variation history */}
      {mockups.length > 1 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Your concepts ({mockups.length})</div>
          <div className="flex flex-wrap gap-2" data-testid="design-history">
            {mockups.map((m) => (
              <button key={m.id} onClick={() => loadMockup(m.id)}
                className={`text-xs px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${current?.id === m.id ? "border-gold text-gold" : "border-white/15 text-gray-400 hover:border-gold/40"}`}>
                {m.selected && <CheckCircle2 size={11} className="text-emerald-400" />}
                Variation {(m.variation || 0) + 1} · {new Date(m.created_at).toLocaleDateString()}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
